import React from "react";
import { CompanionFormStep } from "../companionForm/CompanionFormStep";
import {
  STYLES,
  GIRL_ANIME_BODY_TYPE,
  GIRL_ANIME_EYE_COLOR,
  GIRL_ANIME_HAIR_COLORS,
  GIRL_ANIME_HAIR_LENGTH,
  GIRL_ANIME_HAIRSTYLES,
  GIRL_ANIME_RACE,
  GIRL_ANIME_SKIN_TONE,
  GIRL_REALISM_BODY_TYPE,
  GIRL_REALISM_ETHNICITY,
  GIRL_REALISM_EYE_COLOR,
  GIRL_REALISM_HAIR_COLORS,
  GIRL_REALISM_HAIR_LENGTH,
  GIRL_REALISM_HAIRSTYLES,

  // landing form constants
  LP_FAKE_FORM_STEPS as STEPS,
} from "../../lib/constants";
import { useStepper } from "../ui/stepper";
import { WandSparkles } from "lucide-react";
import { ImageOverlay } from "../ui/imageOverlay";
import { cn } from "../../lib/utils";
import { useGetStep } from "../../hooks/useGetStep";

export const LandingFormSummary = ({ data }) => {
  const { navigate: navigateStep } = useStepper();

  const {
    style,
    ethnicity,
    race,
    body,
    tone,
    eyes,
    hairColor,
    hairLength,
    hairStyle,
    soulName,
  } = data;

  const bodyStep = useGetStep(
    style === STYLES.REALISM ? GIRL_REALISM_BODY_TYPE : GIRL_ANIME_BODY_TYPE,
    body
  );
  const eyesStep = useGetStep(
    style === STYLES.REALISM ? GIRL_REALISM_EYE_COLOR : GIRL_ANIME_EYE_COLOR,
    eyes
  );

  const hairColorStep = useGetStep(
    style === STYLES.REALISM
      ? GIRL_REALISM_HAIR_COLORS
      : GIRL_ANIME_HAIR_COLORS,
    hairColor
  );
  const hairLengthStep = useGetStep(
    style === STYLES.REALISM
      ? GIRL_REALISM_HAIR_LENGTH
      : GIRL_ANIME_HAIR_LENGTH,
    hairLength
  );
  const hairStyleStep = useGetStep(
    style === STYLES.REALISM ? GIRL_REALISM_HAIRSTYLES : GIRL_ANIME_HAIRSTYLES,
    hairStyle
  );

  // Realism
  const ethnicityStep = useGetStep(GIRL_REALISM_ETHNICITY, ethnicity);

  // Anime
  const skinToneStep = useGetStep(GIRL_ANIME_SKIN_TONE, tone);
  const raceStep = useGetStep(GIRL_ANIME_RACE, race);

  return (
    <CompanionFormStep
      id={STEPS.SUMMARY}
      next={() => {
        navigateStep(STEPS.CREATE);
      }}
      nextChildren={
        <>
          <WandSparkles className="size-4 " />
          Bring {soulName} to life
        </>
      }
      back={() => navigateStep(STEPS.SOUL)}
    >
      <h2 className="text-xl lg:text-2xl font-bold text-center">Summary</h2>

      {data.style === STYLES.ANIME && (
        <div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            {/* Race */}
            <ImageOverlay
              src={raceStep?.image}
              alt={raceStep?.label}
              className="w-full h-full object-cover capitalize"
            >
              <p className="text-white font-bold text-2xl">{raceStep?.label}</p>
              <p className="text-white/60 font-medium">Anime</p>
            </ImageOverlay>

            <div className="grid grid-rows-2 gap-4">
              {/* Tone */}
              <ImageOverlay
                src={skinToneStep?.image}
                alt={skinToneStep?.label}
                className="w-full h-full object-cover capitalize aspect-[16/9]"
              >
                <p className="text-white font-bold text-lg leading-tight">
                  {skinToneStep?.label}
                </p>
                <p className="text-white/60 font-medium text-sm leading-tight">
                  Tone
                </p>
              </ImageOverlay>

              {/* Body */}
              <ImageOverlay
                src={bodyStep?.image}
                alt={bodyStep?.label}
                className="w-full h-full object-cover capitalize aspect-[16/9] "
              >
                <p className="text-white font-bold text-lg leading-tight">
                  {bodyStep?.label}
                </p>
                <p className="text-white/60 font-medium text-sm leading-tight">
                  Body
                </p>
              </ImageOverlay>
            </div>
          </div>
        </div>
      )}
      {data.style === STYLES.REALISM && (
        <div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            {/* Race */}
            <ImageOverlay
              src={ethnicityStep?.image}
              alt={ethnicityStep?.label}
              className="w-full h-full object-cover capitalize"
            >
              <p className="text-white font-bold text-2xl">
                {ethnicityStep?.label}
              </p>
              <p className="text-white/60 font-medium">Realism</p>
            </ImageOverlay>

            <div className="flex flex-col gap-4">
              {/* Ethnicity */}
              <ImageOverlay
                src={bodyStep?.image}
                alt={bodyStep?.label}
                className="w-full object-cover capitalize flex-1 "
              >
                <p className="text-white font-bold text-lg leading-tight">
                  {bodyStep?.label}
                </p>
                <p className="text-white/60 font-medium text-sm leading-tight">
                  Body Type
                </p>
              </ImageOverlay>

              <ImageOverlay
                src={eyesStep?.image}
                alt={eyesStep?.label}
                className="w-full  object-cover capitalize h-fit"
              >
                <p className="text-white font-bold leading-tight">
                  {eyesStep?.label}
                </p>
                <p className="text-white/60 font-medium text-sm leading-thigh">
                  Eyes
                </p>
              </ImageOverlay>
            </div>
          </div>
        </div>
      )}

      <div
        className={cn(
          "mt-6 grid gap-4",
          style === STYLES.REALISM && "grid-cols-3",
          style === STYLES.ANIME && "grid-cols-2 grid-rows-2"
        )}
      >
        {/* Hair Color */}
        <ImageOverlay
          src={hairColorStep?.image}
          alt={hairColorStep?.label}
          className={cn(
            "w-full  object-cover capitalize aspect-[4/3]",
            style === STYLES.REALISM && "aspect-[1/1]"
          )}
        >
          <p className="text-white font-bold">{hairColorStep?.label}</p>
          <p className="text-white/60 font-medium text-sm leading-thigh">
            Hair Color
          </p>
        </ImageOverlay>

        {/* Hair Length */}
        <ImageOverlay
          src={hairLengthStep?.image}
          alt={hairLengthStep?.label}
          className={cn(
            "w-full  object-cover capitalize aspect-[4/3]",
            style === STYLES.REALISM && "aspect-[1/1]"
          )}
        >
          <p className="text-white font-bold">{hairLengthStep?.label}</p>
          <p className="text-white/60 font-medium text-sm leading-thigh">
            Hair Length
          </p>
        </ImageOverlay>

        {/* Hair Style */}
        <ImageOverlay
          src={hairStyleStep?.image}
          alt={hairStyleStep?.label}
          className={cn(
            "w-full  object-cover capitalize aspect-[4/3]",
            style === STYLES.REALISM && "aspect-[1/1]"
          )}
        >
          <p className="text-white font-bold">{hairStyleStep?.label}</p>
          <p className="text-white/60 font-medium text-sm leading-thigh">
            Hair Style
          </p>
        </ImageOverlay>

        {/* eyes */}
        {style === STYLES.ANIME && (
          <ImageOverlay
            src={eyesStep?.image}
            alt={eyesStep?.label}
            className={cn(
              "w-full  object-cover capitalize aspect-[4/3] h-fit [&_img]:object-right",
              style === STYLES.REALISM && "aspect-[1/1]"
            )}
          >
            <p className="text-white font-bold leading-tight">
              {eyesStep?.label}
            </p>
            <p className="text-white/60 font-medium text-sm leading-thigh">
              Eyes
            </p>
          </ImageOverlay>
        )}
      </div>
    </CompanionFormStep>
  );
};
